<script>
/**
 * Aviso GENÉRICO do composer: ícone + texto + (opcional) um link de ação +
 * (opcional) o "×" de fechar.
 *
 * É a ABA atrás da caixa do composer, pelo slot `cabecalho` do `send-message`
 * (que é dono do fundo, do raio e do recuo da aba — `.send-message-cabecalho`;
 * aqui só o conteúdo). Dois moradores de créditos (`mixins/aviso-de-creditos.js`):
 *
 * - saldo ESGOTADO (item 4, 14/09/2026): "Seus créditos acabaram…" + "Meus
 *   créditos" — BLOQUEIO, sem "×".
 * - saldo BAIXO (15/09/2026): "Seus créditos estão acabando…" + "Meus créditos"
 *   + o "×" (`fechavel`, emite `fechar`) — aviso, some por 7 dias ao fechar.
 *   ★ Morava na linha do hint ("Créditos acabando" ao lado de "Enter para
 *   enviar"), e o usuário achou poluído: a faixa é o lugar de UM aviso só.
 *
 * A `composer-atencao` é a outra moradora do mesmo slot, e a de créditos
 * vence quando coexistem (quem decide é o `view-chat`).
 *
 * Componente BURRO — não lê store nem `$access`; quem sabe do assunto preenche
 * as props. Mesma disciplina de `composer-atencao` e `ia-insight-card`.
 *
 * ★ Tom, nunca hex: o dado diz o que significa e o token vem daqui. O tom
 * controla SÓ a cor do ícone: a faixa não tem fundo próprio — o cinza quente
 * da lateral (`--sidebar-warm-bg`) é da aba do `send-message`; o usuário viu o
 * wash âmbar ao vivo e o recusou (14/09/2026). O texto é `--neutral-700`/13px
 * e o link segue `--neutral-900` sublinhado. O âmbar do ícone é
 * `--warning-text` (o único da família exportado como custom property —
 * `--warning-new` é só variável SASS, ver `ia-insight-card/tons.js`).
 */
const TOM_WARNING = 'warning'
const TOM_INFO = 'info'
const TOM_DANGER = 'danger'
const TONS = new Set([TOM_WARNING, TOM_INFO, TOM_DANGER])

const COR_POR_TOM = {
  [TOM_WARNING]: 'var(--warning-text)',
  [TOM_INFO]: 'var(--info)',
  [TOM_DANGER]: 'var(--danger-text)',
}
// Todos existem em `src/components/icons/svg/` — nome inexistente renderiza vazio.
const ICONE_POR_TOM = {
  [TOM_WARNING]: 'warning-status',
  [TOM_INFO]: 'info-circle',
  [TOM_DANGER]: 'warning-circle',
}
const TAMANHO_ICONE = 16
const TAMANHO_ICONE_FECHAR = 14

export default {
  name: 'IntelligenceV2ComposerAviso',
  props: {
    texto: {
      type: String,
      required: true,
    },
    // Rótulo do link à direita. Vazio = aviso só informativo, sem link.
    acao: {
      type: String,
      default: '',
    },
    tom: {
      type: String,
      default: TOM_WARNING,
      validator: (valor) => TONS.has(valor),
    },
    // Vence o ícone do tom quando informado.
    icone: {
      type: String,
      default: '',
    },
    // Mostra o "×" (emite `fechar`). Só para AVISO — bloqueio não se fecha.
    fechavel: {
      type: Boolean,
      default: false,
    },
    rotuloDeFechar: {
      type: String,
      default: 'Fechar',
    },
  },
  computed: {
    // Tom fora da lista é barrado pelo `validator` (aviso em dev) — aqui não há
    // segundo fallback silencioso.
    classes() {
      return ['composer-aviso--' + this.tom]
    },
    corDoIcone() {
      return COR_POR_TOM[this.tom]
    },
    iconeEfetivo() {
      return this.icone || ICONE_POR_TOM[this.tom]
    },
    tamanhoDoIcone() {
      return TAMANHO_ICONE
    },
    tamanhoDoIconeDeFechar() {
      return TAMANHO_ICONE_FECHAR
    },
    temAcao() {
      return Boolean(this.acao)
    },
  },
  methods: {
    aoClicarAcao() {
      this.$emit('acao')
    },
    aoFechar() {
      this.$emit('fechar')
    },
  },
}
</script>

<template>
  <!-- `<output>` (região viva nativa, Sonar S6819 — mesmo precedente do
       `output.raciocinio-linha__anuncio`): o aviso muda com o estado (saldo) e o
       leitor de tela o anuncia sem roubar o foco do campo. `<output>` é inline
       por padrão — o `display: flex` da classe é o que segura o layout. -->
  <output class="composer-aviso ia-texto-corpo" :class="classes" data-test="composer-aviso">
    <icon-svg
      :name="iconeEfetivo"
      :size="tamanhoDoIcone"
      :color="corDoIcone"
      class="composer-aviso__icone"
      data-test="composer-aviso-icone"
    />
    <span class="composer-aviso__texto" data-test="composer-aviso-texto">{{
      texto
    }}</span>
    <button
      v-if="temAcao"
      type="button"
      class="composer-aviso__acao"
      data-test="composer-aviso-acao"
      @click="aoClicarAcao"
    >
      {{ acao }}
    </button>
    <button
      v-if="fechavel"
      type="button"
      class="composer-aviso__fechar"
      :aria-label="rotuloDeFechar"
      :title="rotuloDeFechar"
      data-test="composer-aviso-fechar"
      @click="aoFechar"
    >
      <icon-svg name="close" :size="tamanhoDoIconeDeFechar" color="var(--neutral-600)" />
    </button>
  </output>
</template>

<style lang="sass" scoped>
.composer-aviso
  color: var(--neutral-700)
  display: flex
  align-items: center
  gap: 8px
  // O conteúdo da aba `.send-message-cabecalho` do composer: só o padding e a
  // tipografia — fundo, raio e recuo são da aba. Sem borda inferior: a base da
  // aba some atrás da caixa branca. O tom fica no ícone; `--neutral-700` no
  // texto (6,9:1 sobre o cinza quente).
  width: 100%
  padding: 8px 16px

.composer-aviso__icone
  flex-shrink: 0

.composer-aviso__texto
  flex: 1
  min-width: 0

// O link: `<button>` vestido de hiperlink — sublinhado, peso 600, sem caixa.
.composer-aviso__acao
  flex-shrink: 0
  padding: 0
  border: 0
  background: none
  font: inherit
  font-weight: 600
  color: var(--neutral-900)
  text-decoration: underline
  text-underline-offset: 2px
  cursor: pointer
  white-space: nowrap

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

// O "×": botão só-ícone, redondo, sem caixa — hover no par quente da aba.
.composer-aviso__fechar
  display: inline-flex
  align-items: center
  justify-content: center
  flex-shrink: 0
  width: 24px
  height: 24px
  margin: -4px -6px -4px 0
  padding: 0
  border: 0
  border-radius: 50%
  background: none
  cursor: pointer
  transition: background var(--motion-duration-fast) var(--motion-easing-standard)

  &:hover
    background: var(--sidebar-warm-hover)

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px
</style>
